import { useAuth } from "../../../context";
import { EmissionsInsightsApi } from "../../../api/emissions-insights/emissions-insights-api";
import { useQueries, useQuery } from "react-query";
import { is } from "utils";

export const emissionsInsightsKey = {
  emissionTotal: () => ["emissionTotal"],
  consumptionTotal: () => ["consumptionTotal"],
  supplierOverview: () => ["supplierOverview"],
  supplierEngagementReport: () => ["supplierEngagementReport"],
};

export function useGetTotalEmissions(year, dimensionItemIds, config, quarter) {
  const { callApi } = useAuth();

  return useQuery(
    [...emissionsInsightsKey.emissionTotal(), dimensionItemIds, year, quarter],
    () => callApi(EmissionsInsightsApi.getEmissionsTotal, { year, dimensionItemIds, quarter }),
    config
  );
}

export function useGetMultiYearEmissions(years, dimensionItemIds, config, quarter) {
  const { callApi } = useAuth();

  return useQueries(
    years.map((year) => ({
      queryKey: [...emissionsInsightsKey.emissionTotal(), dimensionItemIds, year, quarter],
      queryFn: () => callApi(EmissionsInsightsApi.getEmissionsTotal, { year, dimensionItemIds, quarter }),
      ...config,
    }))
  );
}

// Fetch the SECR consumption payload for each year in `years` (energy / water /
// waste / on-site). Mirrors useGetMultiYearEmissions: the Consumption tab reads
// index [0] for the selected year, [1] for the prior-year YoY comparison, and the
// full array for the 5-year energy trend. Query key includes dimension + quarter
// so the cache re-fetches whenever a filter changes.
export function useGetMultiYearConsumption(years, dimensionItemIds, config, quarter) {
  const { callApi } = useAuth();

  return useQueries(
    years.map((year) => ({
      queryKey: [...emissionsInsightsKey.consumptionTotal(), dimensionItemIds, year, quarter],
      queryFn: () => callApi(EmissionsInsightsApi.getConsumption, { year, dimensionItemIds, quarter }),
      ...config,
    }))
  );
}

// One query per (year, quarter) combination — Q1..Q4 for every year in
// `years`, in year-major order. Powers the Emissions Trends quarterly view.
// Query keys mirror useGetTotalEmissions so quarter data already fetched
// elsewhere (cards, quarterly table) is reused from cache.
export function useGetQuarterlyMultiYearEmissions(years, dimensionItemIds, config) {
  const { callApi } = useAuth();
  const periods = years.flatMap((year) => ["Q1", "Q2", "Q3", "Q4"].map((quarter) => ({ year, quarter })));

  return useQueries(
    periods.map(({ year, quarter }) => ({
      queryKey: [...emissionsInsightsKey.emissionTotal(), dimensionItemIds, year, quarter],
      queryFn: () => callApi(EmissionsInsightsApi.getEmissionsTotal, { year, dimensionItemIds, quarter }),
      ...config,
    }))
  );
}

// Fetch the insights totals for each quarter of `year` plus the whole-year
// figure (null quarter → backend defaults to the full year). Used by the
// footprint quarterly table. Query keys mirror useGetTotalEmissions (null =
// whole year) so data already fetched for the summary cards is reused.
export function useGetQuarterlyEmissions(year, dimensionItemIds, config) {
  const { callApi } = useAuth();
  const periods = ["Q1", "Q2", "Q3", "Q4", null];

  return useQueries(
    periods.map((quarter) => ({
      queryKey: [...emissionsInsightsKey.emissionTotal(), dimensionItemIds, year, quarter],
      queryFn: () => callApi(EmissionsInsightsApi.getEmissionsTotal, { year, dimensionItemIds, quarter }),
      ...config,
    }))
  );
}

// Fetch the SECR consumption payload for each quarter of `year` plus the
// whole-year figure (null quarter → backend defaults to the full year).
// Mirrors useGetQuarterlyEmissions exactly, but against the consumption
// endpoint — used by the Consumption tab's carrier quarterly table. Query
// keys mirror useGetMultiYearConsumption so quarter data already fetched
// elsewhere is reused from cache.
export function useGetQuarterlyConsumption(year, dimensionItemIds, config) {
  const { callApi } = useAuth();
  const periods = ["Q1", "Q2", "Q3", "Q4", null];

  return useQueries(
    periods.map((quarter) => ({
      queryKey: [...emissionsInsightsKey.consumptionTotal(), dimensionItemIds, year, quarter],
      queryFn: () => callApi(EmissionsInsightsApi.getConsumption, { year, dimensionItemIds, quarter }),
      ...config,
    }))
  );
}

export function useGetSupplierOverviewData(years, categories, dataQuality, scope, config) {
  const { callApi } = useAuth();
  // HACK if years is unset, then default: do not fetch
  if (!is(config.enabled)) {
    const enabled = !!(years && years.length > 0 && years[0]); // [null,NaN] => not-enabled
    config = Object.assign({}, config, { enabled });
  }
  return useQuery(
    [...emissionsInsightsKey.supplierOverview(), years, categories, dataQuality, scope],
    () => callApi(EmissionsInsightsApi.getSupplierOverview, { years, categories, dataQuality, scope }),
    config
  );
}

export function useGetSupplierEngagementReport(years, supplierId, config) {
  const { callApi } = useAuth();

  return useQuery(
    [...emissionsInsightsKey.supplierEngagementReport(), years, supplierId],
    () => callApi(EmissionsInsightsApi.getSupplierEngagementReports, { years, supplierId }),
    config
  );
}
